import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus } from 'lucide-react';

export function CreditManagement() {
    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState('');
    const [creditType, setCreditType] = useState('admin');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    const handleAddCredits = async () => {
        if (!email || !amount) {
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Preencha email e quantidade'
            });
            return;
        }

        const creditAmount = parseInt(amount);
        if (isNaN(creditAmount) || creditAmount === 0) {
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: 'Quantidade inválida'
            });
            return;
        }

        setLoading(true);
        try {
            // Simple approach: Get all users and filter by email
            // This works reliably on Supabase free plan
            let foundUser = null;
            let page = 1;
            const perPage = 1000; // Max per page

            // Fetch in pages until we find the user
            while (!foundUser) {
                const { data: batch, error: authError } = await supabase.auth.admin.listUsers({
                    page,
                    perPage
                });

                if (authError) throw authError;
                if (!batch || batch.users.length === 0) break;

                foundUser = batch.users.find(u => u.email?.toLowerCase() === email.toLowerCase());
                if (!foundUser && batch.users.length < perPage) break; // No more pages

                page++;
            }

            if (!foundUser) {
                toast({
                    variant: 'destructive',
                    title: 'Erro',
                    description: 'Usuário não encontrado'
                });
                setLoading(false);
                return;
            }

            // Get profile ID
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('id')
                .eq('user_id', foundUser.id)
                .single();

            if (profileError || !profile) {
                toast({
                    variant: 'destructive',
                    title: 'Erro',
                    description: 'Perfil não encontrado'
                });
                setLoading(false);
                return;
            }

            // Call add_credits_with_tracking RPC
            const { error: rpcError } = await supabase.rpc('add_credits_with_tracking', {
                p_user_id: profile.id,
                p_amount: creditAmount,
                p_credit_type: creditType,
                p_source_id: null,
                p_source_type: 'admin_adjustment',
                p_description: description || 'Ajuste manual de créditos pelo admin',
                p_expires_in_months: null,
                p_available_at: new Date().toISOString()
            });

            if (rpcError) throw rpcError;

            toast({
                title: 'Sucesso ✓',
                description: `${creditAmount} créditos ${creditAmount > 0 ? 'adicionados' : 'removidos'} para ${email}`
            });

            // Clear form
            setEmail('');
            setAmount('');
            setDescription('');
        } catch (error: any) {
            console.error('[CreditManagement] Error:', error);
            toast({
                variant: 'destructive',
                title: 'Erro ao processar',
                description: error.message || 'Falha ao adicionar créditos'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Gerenciar Créditos
                </CardTitle>
                <CardDescription>
                    Adicione ou remova créditos de usuários manualmente
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="user-email">Email do Usuário</Label>
                    <Input
                        id="user-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="usuario@example.com"
                        disabled={loading}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="credit-amount">
                        Quantidade
                        <span className="text-xs text-muted-foreground ml-2">
                            (use valor negativo para remover)
                        </span>
                    </Label>
                    <Input
                        id="credit-amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="10"
                        disabled={loading}
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="credit-type">Tipo de Crédito</Label>
                    <Select value={creditType} onValueChange={setCreditType} disabled={loading}>
                        <SelectTrigger id="credit-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="admin">Admin (ajuste manual)</SelectItem>
                            <SelectItem value="referral">Indicação</SelectItem>
                            <SelectItem value="purchase">Compra</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="description">Descrição (opcional)</Label>
                    <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Motivo do ajuste..."
                        disabled={loading}
                        rows={3}
                    />
                </div>

                <Button
                    onClick={handleAddCredits}
                    disabled={loading}
                    className="w-full"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processando...
                        </>
                    ) : (
                        <>
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar Créditos
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );
}
